import React, { useEffect, useMemo, useState } from 'react';
import { AlertCircle, CheckCircle2, Eye, EyeOff, PackagePlus, Pencil, Search, X } from 'lucide-react';
import { CATALOG_CATEGORIES, getWebsiteCatalogFallback, saveCatalogProduct, seedWebsiteProducts, uploadProductImage, subscribeToCatalogProducts, type CatalogCategory, type CatalogProduct, type FulfillmentType } from '../../../utils/firebase';
type ProductForm = {
  id?: string;
  name: string;
  image: string;
  price: string;
  lookupKey: string;
  fulfillmentType: FulfillmentType;
  stock: string;
  categories: CatalogCategory[];
  visible: boolean;
  shippingWeight: string;
  shippingLength: string;
  shippingWidth: string;
  shippingHeight: string;
};
const EMPTY_FORM: ProductForm = {
  name: '',
  image: '',
  price: '',
  lookupKey: '',
  fulfillmentType: 'physical',
  stock: '0',
  categories: ['Health'],
  visible: true,
  shippingWeight: '',
  shippingLength: '',
  shippingWidth: '',
  shippingHeight: ''
};
function formatPrice(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}
function toForm(product: CatalogProduct): ProductForm {
  return {
    id: product.id,
    name: product.name,
    image: product.image,
    price: (product.price / 100).toFixed(2),
    lookupKey: product.lookupKey ?? '',
    fulfillmentType: product.fulfillmentType,
    stock: String(product.stock),
    categories: product.categories,
    visible: product.visible,
    shippingWeight: product.shipping?.weight?.toString() ?? '',
    shippingLength: product.shipping?.dimensions?.length?.toString() ?? '',
    shippingWidth: product.shipping?.dimensions?.width?.toString() ?? '',
    shippingHeight: product.shipping?.dimensions?.height?.toString() ?? ''
  };
}
function formatUpdatedAt(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}
export function AdminProducts() {
  const [products, setProducts] = useState<CatalogProduct[]>(() => getWebsiteCatalogFallback());
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState<ProductForm | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  useEffect(() => {
    let active = true;
    const fallbackProducts = getWebsiteCatalogFallback();
    const unsubscribe = subscribeToCatalogProducts(next => {
      if (!active) return;
      // A new or unavailable collection must never leave the admin catalog
      // blank—the storefront source remains usable until sync is restored.
      setProducts(next.length > 0 ? next : fallbackProducts);
      setError(null);
    }, catalogError => {
      console.error('[Products] Could not load Firestore catalog:', catalogError);
      if (!active) return;
      setProducts(fallbackProducts);
      setError('Live catalog sync is unavailable. The current website products are shown below, but product changes require the Firestore products rules to be published.');
    });
    seedWebsiteProducts().catch((seedError: unknown) => {
      console.error('[Products] Could not seed website catalog:', seedError);
      if (active) {
        setProducts(fallbackProducts);
        setError('Live catalog sync is unavailable. The current website products are shown below, but product changes require the Firestore products rules to be published.');
      }
    });
    return () => {
      active = false;
      unsubscribe();
    };
  }, []);
  const filteredProducts = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return products;
    return products.filter(product => {
      return product.name.toLowerCase().includes(term) || product.id.toLowerCase().includes(term) || product.fulfillmentType.includes(term) || product.categories.some(category => category.toLowerCase().includes(term)) || (product.visible ? 'visible' : 'hidden').includes(term);
    });
  }, [products, search]);
  const physicalProducts = products.filter(product => product.fulfillmentType === 'physical');
  const totalUnitsInStock = physicalProducts.reduce((sum, product) => sum + product.stock, 0);
  const lowStockProducts = physicalProducts.filter(product => product.stock <= 5);
  const openNewProduct = () => {
    setForm(EMPTY_FORM);
    setImageFile(null);
    setImagePreview(null);
    setError(null);
    setSuccessMessage(null);
  };
  const openEditProduct = (product: CatalogProduct) => {
    setForm(toForm(product));
    setImageFile(null);
    setImagePreview(null);
    setError(null);
    setSuccessMessage(null);
  };
  const updateForm = <K extends keyof ProductForm,>(key: K, value: ProductForm[K]) => {
    setForm(current => current ? {
      ...current,
      [key]: value
    } : current);
  };
  const toggleCategory = (category: CatalogCategory) => {
    setForm(current => {
      if (!current) return current;
      const categories = current.categories.includes(category) ? current.categories.filter(value => value !== category) : [...current.categories, category];
      return {
        ...current,
        categories
      };
    });
  };
  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const allowedTypes = ['image/png', 'image/jpeg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Choose a PNG, JPG, or WebP image.');
      event.target.value = '';
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('Product images must be 10 MB or smaller.');
      event.target.value = '';
      return;
    }
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
    }
    const preview = URL.createObjectURL(file);
    setImageFile(file);
    setImagePreview(preview);
    setError(null);
  };
  const handleSave = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!form) return;
    const price = Number(form.price);
    const stock = Number(form.stock);
    if (!form.name.trim()) {
      setError('Enter a product name before saving.');
      return;
    }
    if (!form.image.trim() && !imageFile) {
      setError('Choose a product image before saving.');
      return;
    }
    if (!Number.isFinite(price) || price < 0) {
      setError('Enter a valid, non-negative price.');
      return;
    }
    if (!Number.isInteger(stock) || stock < 0) {
      setError('Stock must be a whole number of zero or more.');
      return;
    }
    if (form.categories.length === 0) {
      setError('Choose at least one category where this product should appear.');
      return;
    }
    if (form.fulfillmentType === 'physical' && form.categories.every(category => category === 'Books')) {
      setError('Physical products need at least one supplement category.');
      return;
    }
    if (form.fulfillmentType === 'digital' && form.id !== 'glp1-nutrition-guide') {
      setError('The GLP-1 Nutrition Guide is the only digital item in this catalog.');
      return;
    }
    if (form.fulfillmentType === 'digital' && !form.categories.includes('Books')) {
      setError('The GLP-1 Nutrition Guide must appear in the Books category.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      let imageUrl = form.image.trim();
      if (imageFile) {
        imageUrl = await uploadProductImage(imageFile, form.name);
      }
      const result = await saveCatalogProduct({
        id: form.id,
        name: form.name.trim(),
        image: imageUrl,
        price: Math.round(price * 100),
        lookupKey: form.lookupKey.trim() || undefined,
        fulfillmentType: form.fulfillmentType,
        stock,
        shipping: {
          weight: Number(form.shippingWeight) || 0,
          dimensions: {
            length: Number(form.shippingLength) || 0,
            width: Number(form.shippingWidth) || 0,
            height: Number(form.shippingHeight) || 0
          }
        },
        categories: form.categories,
        visible: form.visible
      });
      const {
        product,
        persistence
      } = result;
      setProducts(current => [...current.filter(item => item.id !== product.id), product].sort((a, b) => a.name.localeCompare(b.name)));
      setForm(null);
      setSuccessMessage(persistence === 'firestore' ? form.id ? `${product.name} has been updated.` : `${product.name} has been added to your product catalog.` : `${product.name} was saved on this device. Firestore access is unavailable, so it will not appear for other visitors until catalog sync is restored.`);
    } catch (saveError: unknown) {
      console.error('[Products] Could not save product locally:', saveError);
      setError('The product could not be saved in this browser. Check available browser storage, then try again.');
    } finally {
      setSaving(false);
    }
  };
  if (loading) {
    return <div className="p-8 flex justify-center" data-id="element-421" data-mp="dlunjgz3enoe">
        <span className="w-6 h-6 border-2 border-tealslate/30 border-t-tealslate rounded-full animate-spin" data-id="element-422" data-mp="3uzgkx4esneuy" />
      </div>;
  }
  return <div className="p-6 md:p-8 max-w-7xl mx-auto" data-id="element-423" data-mp="3nwzoul5aswb">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-5 mb-8" data-id="element-424" data-mp="3m0mhau31po7w">
        <div data-id="element-425" data-mp="3q0n6n9ronjtq">
          <h1 className="text-2xl font-bold text-[#111111]" data-id="element-426" data-mp="3q2gbcjiqjtz3">Products</h1>
          <p className="text-sm text-[#5A5A5A] mt-2 max-w-2xl" data-id="element-427" data-mp="3c13ywi2saahy">
            Manage the website catalog and physical inventory. Stock is reduced
            automatically once a paid order is recorded.
          </p>
        </div>
        <button type="button" onClick={openNewProduct} className="inline-flex shrink-0 items-center justify-center gap-2 px-4 py-2.5 bg-tealslate text-white rounded-md text-sm font-bold hover:bg-navyslate transition-colors focus:outline-none focus:ring-2 focus:ring-tealslate focus:ring-offset-2" data-id="element-428" data-mp="2nnoonc6fjbig">
          <PackagePlus className="w-4 h-4" data-id="element-429" data-mp="2ir81olayv36y" /> Add product
        </button>
      </div>

      {successMessage && <div className="mb-6 p-3 bg-green-50 border border-green-200 text-sm text-green-800 rounded-md flex items-center gap-2" data-id="element-430" data-mp="3be2hv60r3gem">
          <CheckCircle2 className="w-4 h-4 shrink-0" data-id="element-431" data-mp="2wpxxzypyoyu9" />
          <span data-id="element-432" data-mp="2wnbr81awand3">{successMessage}</span>
          <button type="button" onClick={() => setSuccessMessage(null)} className="ml-auto p-1 rounded text-green-800 hover:bg-green-100" aria-label="Dismiss success message" data-id="element-433" data-mp="31nhcmzqkndmq">
            <X className="w-4 h-4" data-id="element-434" data-mp="3u64v07e6oek4" />
          </button>
        </div>}

      {error && !form && <div className="mb-6 p-3 bg-amber-50 border border-amber-200 text-sm text-amber-900 rounded-md flex items-start gap-2" data-id="element-435" data-mp="1inqii3ntz0qv">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" data-id="element-436" data-mp="1rhvcgvy9gsam" />
          {error}
        </div>}

      <section className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8" aria-label="Product inventory summary" data-id="element-437" data-mp="1ng9renbkp7pc">
        <div className="bg-white border border-[#E5E5E5] rounded-xl p-5 shadow-sm" data-id="element-438" data-mp="zqfl6olrehe2">
          <p className="text-xs font-bold uppercase tracking-wider text-[#5A5A5A]" data-id="element-439" data-mp="urm4knb6ea9o">
            Catalog products
          </p>
          <p className="text-3xl font-bold text-[#111111] mt-2" data-id="element-440" data-mp="18rnf6uh8sw2c">{products.length}</p>
        </div>
        <div className="bg-white border border-[#E5E5E5] rounded-xl p-5 shadow-sm" data-id="element-441" data-mp="13s12cm5bthu2">
          <p className="text-xs font-bold uppercase tracking-wider text-[#5A5A5A]" data-id="element-442" data-mp="2k6ag6mbxzo6n">
            Physical units in stock
          </p>
          <p className="text-3xl font-bold text-[#111111] mt-2" data-id="element-443" data-mp="2f6o107cjzxv7">
            {totalUnitsInStock}
          </p>
        </div>
        <div className="bg-white border border-[#E5E5E5] rounded-xl p-5 shadow-sm" data-id="element-444" data-mp="1ctrwbe0mnu6i">
          <p className="text-xs font-bold uppercase tracking-wider text-[#5A5A5A]" data-id="element-445" data-mp="1hrkxttqzk00g">
            Low stock items
          </p>
          <p className="text-3xl font-bold text-[#111111] mt-2" data-id="element-446" data-mp="1lsws4ckt19ez">
            {lowStockProducts.length}
          </p>
          <p className="text-xs text-[#9A9A90] mt-1" data-id="element-447" data-mp="u3jssut0n1ps">5 units or fewer</p>
        </div>
      </section>

      {form && <section className="mb-8 bg-white border border-[#D3E2E3] rounded-xl shadow-sm overflow-hidden" aria-labelledby="product-editor-heading" data-id="element-448" data-mp="y4djtx1r8wmw">
          <div className="px-5 py-4 border-b border-[#E5E5E5] bg-[#F7FAFA] flex items-center justify-between gap-4" data-id="element-449" data-mp="132x47t0pa0d5">
            <div data-id="element-450" data-mp="17wt4j24frzbh">
              <h2 id="product-editor-heading" className="font-bold text-[#111111]" data-id="element-451" data-mp="afu8t9rbm5r4">
                {form.id ? 'Edit product' : 'Add a product'}
              </h2>
              {form.id && <p className="text-xs text-[#5A5A5A] mt-1" data-id="element-452" data-mp="7uwce04eoxoi">ID: {form.id}</p>}
            </div>
            <button type="button" onClick={() => {
          setForm(null);
          setError(null);
        }} className="p-2 rounded-md text-[#5A5A5A] hover:bg-white hover:text-[#111111] transition-colors" aria-label="Close product editor" data-id="element-453" data-mp="2vjqbhlczumz">
              <X className="w-5 h-5" data-id="element-454" data-mp="3l2g8azcj7n3u" />
            </button>
          </div>
          <form onSubmit={handleSave} className="p-5 grid grid-cols-1 lg:grid-cols-[160px_1fr] gap-6" data-id="element-455" data-mp="3gag0kkq7zpwp">
<div className="w-full aspect-square rounded-lg border border-[#E5E5E5] bg-[#FAFAFA] overflow-hidden flex items-center justify-center" data-id="element-456" data-mp="3bbbsxyiy208h">
  {imagePreview || form.image ? <img src={imagePreview || form.image} alt="Product preview" className="w-full h-full object-contain p-3" data-id="element-457" data-mp="36axkifdfko9x" /> : <span className="text-xs text-[#9A9A90] text-center px-4" data-id="element-458" data-mp="2xbtzv6ym80tb">
      Image preview
    </span>}
</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-459" data-mp="11a8phk4zjzm4">
              <label className="block md:col-span-2" data-id="element-460" data-mp="15b2dedifhef0">
                <span className="block text-sm font-bold text-[#111111] mb-2" data-id="element-461" data-mp="159r8gad8y0mb">Product name</span>
                <input required value={form.name} onChange={event => updateForm('name', event.target.value)} className="w-full px-3.5 py-3 bg-white border border-[#E5E5E5] rounded-md text-sm text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate" placeholder="e.g. Magnesium Glycinate" data-id="element-462" data-mp="rivysvuxbfho" />
              </label>
<label className="block md:col-span-2" data-id="element-463" data-mp="dj39n4z1p7bt">
  <span className="block text-sm font-bold text-[#111111] mb-2" data-id="element-464" data-mp="dk61pg83tftb">
    Product image
  </span>

  <input type="file" accept="image/png,image/jpeg,image/webp" onChange={handleImageChange} className="w-full px-3.5 py-3 bg-white border border-[#E5E5E5] rounded-md text-sm text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate" data-id="element-465" data-mp="22zm8wq2gwrgk" />

  <span className="block text-xs text-[#5A5A5A] mt-1.5" data-id="element-466" data-mp="2d312sjxe81nv">
    PNG, JPG, or WebP. Maximum 10 MB.
  </span>
</label>
              <label className="block" data-id="element-467" data-mp="2qv7e9splpkgz">
                <span className="block text-sm font-bold text-[#111111] mb-2" data-id="element-468" data-mp="2m2xuyvizrino">Price (USD)</span>
                <input required min="0" step="0.01" inputMode="decimal" type="number" value={form.price} onChange={event => updateForm('price', event.target.value)} className="w-full px-3.5 py-3 bg-white border border-[#E5E5E5] rounded-md text-sm text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate" placeholder="0.00" data-id="element-469" data-mp="30rtzwjpr4fk2" />
              </label>
              <label className="block" data-id="element-470" data-mp="1begsumq98df9">
  <span className="block text-sm font-bold text-[#111111] mb-2" data-id="element-471" data-mp="1bf8mhs0ak12w">
    Stripe Lookup Key
  </span>
  <input type="text" value={form.lookupKey} onChange={event => updateForm('lookupKey', event.target.value)} className="w-full px-3.5 py-3 bg-white border border-[#E5E5E5] rounded-md text-sm text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate" placeholder="e.g. creatine_v1" data-id="element-472" data-mp="3ak0ci3txphz5" />
  <span className="block text-xs text-[#5A5A5A] mt-1.5" data-id="element-473" data-mp="3th9x3oazx1hz">
    Must match the lookup key assigned to the current price in Stripe.
  </span>
</label>
              <label className="block" data-id="element-474" data-mp="2848hrrodzt0">
                <span className="block text-sm font-bold text-[#111111] mb-2" data-id="element-475" data-mp="70niker5mct2">Product type</span>
                <select value={form.fulfillmentType} onChange={event => updateForm('fulfillmentType', event.target.value as FulfillmentType)} className="w-full px-3.5 py-3 bg-white border border-[#E5E5E5] rounded-md text-sm text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate" data-id="element-476" data-mp="c0jqgp2z2odw">
                  <option value="physical" data-id="element-477" data-mp="21gm9pyz9n6uv">Physical</option>
                  <option value="digital" data-id="element-478" data-mp="21j8fbt2ljcag">Digital</option>
                </select>
              </label>
              <label className="block" data-id="element-479" data-mp="1wk5bud29le4l">
                <span className="block text-sm font-bold text-[#111111] mb-2" data-id="element-480" data-mp="1rjhnjz5f7be2">Units in stock</span>
                <input required min="0" step="1" inputMode="numeric" type="number" value={form.stock} onChange={event => updateForm('stock', event.target.value)} className="w-full px-3.5 py-3 bg-white border border-[#E5E5E5] rounded-md text-sm text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate" data-id="element-481" data-mp="2p788ezndres7" />
              </label>

{form.fulfillmentType === 'physical' && <fieldset className="md:col-span-2 border border-[#D3E2E3] rounded-lg p-4" data-id="element-482" data-mp="334elqepc4la1">
    <legend className="px-2 text-sm font-bold text-[#111111]" data-id="element-483" data-mp="2bhd8knrbqkyj">
      Shipping
    </legend>

    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-2" data-id="element-484" data-mp="2gaep1076isk8">

      <label className="block" data-id="element-485" data-mp="22irsr0d8lk1g">
        <span className="block text-sm font-bold text-[#111111] mb-2" data-id="element-486" data-mp="22jjbhalqjnab">
          Weight (lbs)
        </span>
        <input type="number" min="0" step="0.01" value={form.shippingWeight} onChange={event => updateForm('shippingWeight', event.target.value)} className="w-full px-3.5 py-3 bg-white border border-[#E5E5E5] rounded-md text-sm" placeholder="2.2" data-id="element-487" data-mp="26kcze3z6h237" />
      </label>

      <label className="block" data-id="element-488" data-mp="2q71bftf5lrod">
        <span className="block text-sm font-bold text-[#111111] mb-2" data-id="element-489" data-mp="2q69dwd1zxjvg">
          Length (in)
        </span>
        <input type="number" min="0" step="0.01" value={form.shippingLength} onChange={event => updateForm('shippingLength', event.target.value)} className="w-full px-3.5 py-3 bg-white border border-[#E5E5E5] rounded-md text-sm" placeholder="10.5" data-id="element-490" data-mp="19s9h3ayg6acf" />
      </label>

      <label className="block" data-id="element-491" data-mp="1g8rti4mp8wbx">
        <span className="block text-sm font-bold text-[#111111] mb-2" data-id="element-492" data-mp="1g91gdjcg6nde">
          Width (in)
        </span>
        <input type="number" min="0" step="0.01" value={form.shippingWidth} onChange={event => updateForm('shippingWidth', event.target.value)} className="w-full px-3.5 py-3 bg-white border border-[#E5E5E5] rounded-md text-sm" placeholder="9.25" data-id="element-493" data-mp="1b8n75xz417ds" />
      </label>

      <label className="block" data-id="element-494" data-mp="sjgno8qhsbqx">
        <span className="block text-sm font-bold text-[#111111] mb-2" data-id="element-495" data-mp="si5kaa0yl60c">
          Height (in)
        </span>
        <input type="number" min="0" step="0.01" value={form.shippingHeight} onChange={event => updateForm('shippingHeight', event.target.value)} className="w-full px-3.5 py-3 bg-white border border-[#E5E5E5] rounded-md text-sm" placeholder="3" data-id="element-496" data-mp="4sb9rz443tj9" />
      </label>

    </div>

    <p className="text-xs text-[#5A5A5A] mt-3" data-id="element-497" data-mp="327x02qynrfg6">
      Enter the product weight and dimensions only. Do not include shipping mailers or packing materials.
    </p>
  </fieldset>}

              <fieldset className="md:col-span-2" data-id="element-498" data-mp="3b5pg5xc7er4m">
                <legend className="block text-sm font-bold text-[#111111] mb-2" data-id="element-499" data-mp="368ftgpg78tkh">
                  Storefront categories
                </legend>
                <p className="text-xs text-[#5A5A5A] mb-3" data-id="element-500" data-mp="3l4y6mg7v1nm8">
                  Select every section where customers should find this product.
                </p>
                <div className="flex flex-wrap gap-2" role="group" aria-label="Storefront categories" data-id="element-501" data-mp="1qqx85wg832mo">
                  {CATALOG_CATEGORIES.map(category => {
                const selected = form.categories.includes(category);
                return <button key={category} type="button" onClick={() => toggleCategory(category)} aria-pressed={selected} className={`px-3 py-2 rounded-md border text-sm font-bold transition-colors focus:outline-none focus:ring-2 focus:ring-tealslate focus:ring-offset-2 ${selected ? 'bg-tealslate border-tealslate text-white' : 'bg-white border-[#D3E2E3] text-[#3B4A4F] hover:border-tealslate'}`} data-id="element-502" data-mp="1cnvy2ygf6e8">
                        {category}
                      </button>;
              })}
                </div>
              </fieldset>
              <div className="md:col-span-2 border border-[#D3E2E3] rounded-lg p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-[#F7FAFA]" data-id="element-503" data-mp="39s7nmxjh8gr6">
                <div data-id="element-504" data-mp="17xfey1tacm90">
                  <p className="text-sm font-bold text-[#111111]" data-id="element-505" data-mp="17xytd8jq8sia">Site visibility</p>
                  <p className="text-xs text-[#5A5A5A] mt-1" data-id="element-506" data-mp="16j5wo7tn78d7">
                    {form.visible ? 'This product is visible to customers on the site.' : 'This product is hidden from customers but remains in your catalog.'}
                  </p>
                </div>
                <button type="button" onClick={() => updateForm('visible', !form.visible)} role="switch" aria-checked={form.visible} className={`inline-flex shrink-0 items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-bold transition-colors focus:outline-none focus:ring-2 focus:ring-tealslate focus:ring-offset-2 ${form.visible ? 'bg-tealslate text-white hover:bg-navyslate' : 'bg-white text-[#5A5A5A] border border-[#D3E2E3] hover:border-tealslate'}`} data-id="element-507" data-mp="rm52zyrw53c3">
                  {form.visible ? <Eye className="w-4 h-4" data-id="element-508" data-mp="236iy23qtdte3" /> : <EyeOff className="w-4 h-4" data-id="element-509" data-mp="2bgho8wqxo3d2" />}
                  {form.visible ? 'Visible on site' : 'Hidden from site'}
                </button>
              </div>
              <div className="flex items-end gap-3" data-id="element-510" data-mp="2fil9ue7bgoc2">
                <button type="submit" disabled={saving} className="inline-flex items-center justify-center px-5 py-3 bg-tealslate text-white rounded-md text-sm font-bold hover:bg-navyslate disabled:opacity-60 disabled:cursor-not-allowed transition-colors" data-id="element-511" data-mp="2kgmu32ghatp4">
                  {saving ? 'Saving…' : 'Save product'}
                </button>
                <button type="button" disabled={saving} onClick={() => {
              setForm(null);
              setError(null);
            }} className="px-4 py-3 text-sm font-bold text-[#5A5A5A] hover:text-[#111111] disabled:opacity-60" data-id="element-512" data-mp="337e2a0oqce1x">
                  Cancel
                </button>
              </div>
              {error && <div className="md:col-span-2 p-3 bg-red-50 border border-red-200 text-sm text-red-800 rounded-md flex items-start gap-2" data-id="element-513" data-mp="iouj8h4ampp8">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" data-id="element-514" data-mp="sh8ohjukyeg5" /> {error}
                </div>}
            </div>
          </form>
        </section>}

      <section className="bg-white border border-[#E5E5E5] rounded-xl shadow-sm overflow-hidden" data-id="element-515" data-mp="nk055fxp44db">
        <div className="p-4 border-b border-[#E5E5E5] bg-[#FAFAFA] flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between" data-id="element-516" data-mp="3vzacb2gfzzry">
          <p className="text-sm font-medium text-[#5A5A5A]" data-id="element-517" data-mp="8szvx8p4k6g4">
            {filteredProducts.length} {filteredProducts.length === 1 ? 'product' : 'products'}
          </p>
          <div className="relative w-full sm:w-80" data-id="element-518" data-mp="1k7wnoqcp19r6">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9A9A90]" data-id="element-519" data-mp="1k6lhykzotrxf" />
            <input value={search} onChange={event => setSearch(event.target.value)} className="w-full pl-10 pr-4 py-2 bg-white border border-[#E5E5E5] rounded-md text-sm focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate" placeholder="Search products" aria-label="Search products" data-id="element-520" data-mp="1g5jj459onhvz" />
          </div>
        </div>
        <div className="overflow-x-auto" data-id="element-521" data-mp="1iqzn37opmqlt">
          <table className="w-full text-left border-collapse" data-id="element-522" data-mp="26fsb8hz3bu5v">
            <thead data-id="element-523" data-mp="26ierr73ylk08">
              <tr className="bg-[#FAFAFA] border-b border-[#E5E5E5]" data-id="element-524" data-mp="2bfexfgwwckqt">
                <th className="px-5 py-3 text-xs font-bold uppercase tracking-wider text-[#5A5A5A]" data-id="element-525" data-mp="2bhho835vihwl">Product</th>
                <th className="px-5 py-3 text-xs font-bold uppercase tracking-wider text-[#5A5A5A]" data-id="element-526" data-mp="1woo7keev7vqc">Price</th>
                <th className="px-5 py-3 text-xs font-bold uppercase tracking-wider text-[#5A5A5A]" data-id="element-527" data-mp="2pf6b1gji3z4a">Type</th>
                <th className="px-5 py-3 text-xs font-bold uppercase tracking-wider text-[#5A5A5A]" data-id="element-528" data-mp="2u5mo2zjetiq4">Categories</th>
                <th className="px-5 py-3 text-xs font-bold uppercase tracking-wider text-[#5A5A5A]" data-id="element-529" data-mp="3424yl0f8m9dq">Visibility</th>
                <th className="px-5 py-3 text-xs font-bold uppercase tracking-wider text-[#5A5A5A]" data-id="element-530" data-mp="2z3v3eptij8s4">Inventory</th>
                <th className="px-5 py-3 text-xs font-bold uppercase tracking-wider text-[#5A5A5A]" data-id="element-531" data-mp="2uznashjxp8om">Updated</th>
                <th className="px-5 py-3" data-id="element-532" data-mp="2q1u83yhuawt3"><span className="sr-only" data-id="element-533" data-mp="2l30rhx79apop">Edit product</span></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E5E5E5]" data-id="element-534" data-mp="2gcaogqmayyx6">
              {filteredProducts.length === 0 ? <tr data-id="element-535" data-mp="2bcy021fs9jsh">
                  <td colSpan={8} className="px-6 py-14 text-center text-sm text-[#5A5A5A]" data-id="element-536" data-mp="2bcfsw9rl1l5s">
                    {products.length === 0 ? 'No products are available yet.' : 'No products match your search.'}
                  </td>
                </tr> : filteredProducts.map(product => <tr key={product.id} className="hover:bg-[#FAFAFA] transition-colors" data-id="element-537" data-mp="1xdydr0fqtgzh">
                    <td className="px-5 py-4 min-w-[260px]" data-id="element-538" data-mp="cum7g4zyjdzp">
                      <div className="flex items-center gap-3" data-id="element-539" data-mp="hu8kadbvis7z">
                        <div className="w-12 h-12 rounded-md border border-[#E5E5E5] bg-white shrink-0 flex items-center justify-center overflow-hidden" data-id="element-540" data-mp="hwlb7fzm0cx6">
                          <img src={product.image} alt="" className="w-full h-full object-contain p-1" data-id="element-541" data-mp="qvgeaj7cx6w9" />
                        </div>
                        <div data-id="element-542" data-mp="3qc4ylydln14b">
                          <p className="text-sm font-bold text-[#111111]" data-id="element-543" data-mp="3v9eo1e0zncs5">{product.name}</p>
                          <p className="text-xs text-[#9A9A90] mt-0.5" data-id="element-544" data-mp="3v70w3e1lugot">{product.id}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-sm font-medium text-[#111111] whitespace-nowrap" data-id="element-545" data-mp="7xvptd8i8ac6">{formatPrice(product.price)}</td>
                    <td className="px-5 py-4" data-id="element-546" data-mp="3ble8curnoa3b">
                      <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${product.fulfillmentType === 'physical' ? 'bg-[#F0F4F8] text-tealslate' : 'bg-violet-50 text-violet-700'}`} data-id="element-547" data-mp="3bkcmrwd0k57c">
                        {product.fulfillmentType === 'physical' ? 'Physical' : 'Digital'}
                      </span>
                    </td>
                    <td className="px-5 py-4 min-w-[180px]" data-id="element-548" data-mp="29x6b7bissszq">
                      <div className="flex flex-wrap gap-1" data-id="element-549" data-mp="29vlhfo4b5e2x">
                        {product.categories.map(category => <span key={category} className="inline-flex px-2 py-1 rounded bg-[#F0F4F8] text-[11px] font-medium text-tealslate" data-id="element-550" data-mp="2nul44q89fiwg">
                            {category}
                          </span>)}
                      </div>
                    </td>
                    <td className="px-5 py-4" data-id="element-551" data-mp="2spqzsgrgsbxq">
                      <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${product.visible ? 'bg-green-50 text-green-700' : 'bg-[#F5F5F5] text-[#5A5A5A]'}`} data-id="element-552" data-mp="36mnr0jbhvvf5">
                        {product.visible ? 'Visible' : 'Hidden'}
                      </span>
                    </td>
                    <td className="px-5 py-4" data-id="element-553" data-mp="3q9h7ecakh5ca">
                      {product.fulfillmentType === 'digital' ? <span className="text-sm text-[#5A5A5A]" data-id="element-554" data-mp="3c8o8lfd8fo3d">Not tracked</span> : <span className={`text-sm font-bold ${product.stock <= 5 ? 'text-burgundy' : 'text-[#111111]'}`} data-id="element-555" data-mp="3h6zhnnvpdukj">
                          {product.stock} {product.stock === 1 ? 'unit' : 'units'}
                        </span>}
                    </td>
                    <td className="px-5 py-4 text-sm text-[#5A5A5A] whitespace-nowrap" data-id="element-556" data-mp="2tjzqf164vwva">{formatUpdatedAt(product.updatedAt)}</td>
                    <td className="px-5 py-4 text-right" data-id="element-557" data-mp="2xktdxtfnz9nn">
                      <button type="button" onClick={() => openEditProduct(product)} className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-bold text-tealslate hover:bg-[#F0F4F8] rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-tealslate" data-id="element-558" data-mp="2jkio8tq3c142">
                        <Pencil className="w-3.5 h-3.5" data-id="element-559" data-mp="2uwmff1ckgabj" /> Edit
                      </button>
                    </td>
                  </tr>)}
            </tbody>
          </table>
        </div>
      </section>
    </div>;
}